import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { AgingBar } from "../../components/admin/payments/AgingBar";
import { OverdueOwners } from "../../components/admin/payments/OverdueOwners";
import { TimelineDrawer } from "../../components/admin/payments/TimelineDrawer";
import { SendStatementModal } from "../../components/admin/payments/SendStatementModal";
import { ReplyModal } from "../../components/admin/payments/ReplyModal";
import { fmt, backend } from "../../components/admin/payments/utils";
import { toast } from "sonner";
import { Plus, Download, IndianRupee, Wrench, Eye, AlertTriangle, CheckCircle2, FileText, BarChart3, Mail, Send, Flame } from "lucide-react";

const METHODS = ["bank_transfer", "cash", "cheque", "upi", "other"];
const EMPTY = { user_id: "", kind: "plot_purchase", amount: "", plot_id: "", method: "bank_transfer", reference: "", note: "" };
const kindLabel = (k) => k === "plot_purchase" ? "Plot purchase" : "Maintenance";

export default function AdminPaymentsPage() {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [plots, setPlots] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [genOpen, setGenOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [gen, setGen] = useState({ project_id: "", period_start: "", period_end: "" });
  const [preview, setPreview] = useState(null); // { project, rate, period_start, period_end, rows }
  const [reportOpen, setReportOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [overdue, setOverdue] = useState([]);
  const [overdueSummary, setOverdueSummary] = useState(null);
  const [drawerOwner, setDrawerOwner] = useState(null);
  const [drawerInvoices, setDrawerInvoices] = useState([]);
  const [drawerHistory, setDrawerHistory] = useState([]);
  const [drawerBusy, setDrawerBusy] = useState(false);
  const [eventFilter, setEventFilter] = useState("all");
  const [bucketFilter, setBucketFilter] = useState("all");
  const [sendStmtOpen, setSendStmtOpen] = useState(false);
  const [stmtNote, setStmtNote] = useState("");
  const [customTemplates, setCustomTemplates] = useState(() => {
    try { return JSON.parse(localStorage.getItem("stmt_note_templates") || "[]"); }
    catch { return []; }
  });
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [replyPreview, setReplyPreview] = useState(null);
  const [replyAmountOverride, setReplyAmountOverride] = useState("");
  const [replyReferenceOverride, setReplyReferenceOverride] = useState("");
  const today = new Date();
  const [reportForm, setReportForm] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [p, u, pl, pr, od, os] = await Promise.all([
      api.get("/payments"), api.get("/users?role=customer"),
      api.get("/plots"), api.get("/projects"),
      api.get("/overdue-owners").catch(() => ({ data: [] })),
      api.get("/overdue-summary").catch(() => ({ data: null })),
    ]);
    setPayments(p.data); setUsers(u.data); setPlots(pl.data); setProjects(pr.data);
    setOverdue(od.data || []);
    setOverdueSummary(os.data || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount), plot_id: form.plot_id || null });
      toast.success("Payment captured — receipt emailed");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitPreview = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/maintenance-invoices/preview", gen);
      setPreview(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const confirmGenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/maintenance-invoices/generate", gen);
      toast.success(`Created ${data.created} invoice(s), skipped ${data.skipped}`);
      setGenOpen(false); setPreview(null);
      setGen({ project_id: "", period_start: "", period_end: "" });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadPreviewCSV = () => {
    if (!preview) return;
    const rows = preview.rows || [];
    const header = ["owner", "email", "plot", "kind", "size_sqft", "amount", "issue", "already_invoiced"];
    const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const lines = [header.join(",")];
    rows.forEach((r) => {
      const owner = users.find((u) => u.id === r.user_id);
      lines.push([
        esc(r.user_name), esc(owner?.email || ""),
        esc(r.plot_number || ""), esc(r.kind),
        r.size_sqft, r.amount,
        esc(r.issue || ""), r.already_invoiced ? "yes" : "no",
      ].join(","));
    });
    const meta = `# ${preview.project} · ${preview.period_start} → ${preview.period_end} · rate ${preview.rate}/sqft\n`;
    const blob = new Blob([meta + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `preview-${preview.project?.replaceAll(" ", "_") || "invoices"}-${preview.period_start}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runReport = async (e) => {
    if (e) e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.get(`/maintenance-invoices/common-report?year=${reportForm.year}&month=${reportForm.month}`);
      setReport(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const downloadReportCSV = () => {
    if (!report) return;
    const header = ["project", "owner", "email", "amount", "status"];
    const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
    const lines = [header.join(",")];
    (report.projects || []).forEach((p) => {
      (p.rows || []).forEach((r) => {
        lines.push([esc(p.project_name), esc(r.user_name), esc(r.user_email || ""), r.amount, esc(r.status)].join(","));
      });
    });
    const meta = `# Common maintenance · ${report.period_start} → ${report.period_end} · ${report.grand_count} owner(s) · Total ${report.grand_total}\n`;
    const blob = new Blob([meta + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `common-maintenance-${reportForm.year}-${String(reportForm.month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredOverdue = useMemo(() => overdue.filter((o) => {
    if (bucketFilter === "all") return true;
    const d = o.max_days_overdue;
    if (bucketFilter === "0_30") return d <= 30;
    if (bucketFilter === "31_60") return d > 30 && d <= 60;
    if (bucketFilter === "61_90") return d > 60 && d <= 90;
    return d > 90;
  }), [overdue, bucketFilter]);

  const sendBulkReminders = async () => {
    if (!window.confirm("Email every owner with a due invoice a reminder now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/bulk-reminders");
      toast.success(`Reminder sent to ${data.sent} of ${data.owners} owner(s)`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const runEscalations = async () => {
    if (!window.confirm("Escalate every invoice over 60 days overdue to the account admin now?")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/run-escalations");
      toast.success(`Escalated ${data.escalated_invoices} invoice(s) to ${data.targets} admin(s)`);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const openTimeline = async (owner) => {
    setDrawerOwner(owner);
    setDrawerBusy(true);
    setDrawerInvoices([]);
    setDrawerHistory([]);
    setEventFilter("all");
    try {
      const [inv, hist] = await Promise.all([
        api.get(`/maintenance-invoices?user_id=${owner.user_id}`),
        api.get(`/owners/${owner.user_id}/statement-history`).catch(() => ({ data: [] })),
      ]);
      setDrawerInvoices(inv.data);
      setDrawerHistory(hist.data || []);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setDrawerBusy(false); }
  };

  const openSendStmt = () => { setStmtNote(""); setSendStmtOpen(true); };

  const saveCustomTemplate = () => {
    const t = stmtNote.trim();
    if (!t || customTemplates.includes(t)) return;
    const next = [t, ...customTemplates].slice(0, 6);
    setCustomTemplates(next);
    try { localStorage.setItem("stmt_note_templates", JSON.stringify(next)); }
    catch (e) { console.warn("stmt_note_templates persist failed:", e); }
    toast.success("Template saved");
  };

  const previewReply = async () => {
    if (!drawerOwner || !replyText.trim()) return;
    setDrawerBusy(true);
    try {
      const { data } = await api.post(`/owners/${drawerOwner.user_id}/statement-reply`,
        { reply_body: replyText.trim(), dry_run: true });
      setReplyPreview(data);
      setReplyAmountOverride(String(data.amount ?? ""));
      setReplyReferenceOverride(data.reference || "");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setDrawerBusy(false); }
  };

  const rePreviewWithOverride = async () => {
    if (!drawerOwner) return;
    setDrawerBusy(true);
    try {
      const { data } = await api.post(`/owners/${drawerOwner.user_id}/statement-reply`,
        { amount: Number(replyAmountOverride), reference: replyReferenceOverride.trim(),
          dry_run: true });
      setReplyPreview(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setDrawerBusy(false); }
  };

  const submitReply = async () => {
    if (!drawerOwner) return;
    setDrawerBusy(true);
    try {
      // Send with the current (possibly overridden) amount + reference — the
      // server re-computes allocations, so we're always in sync with what the
      // admin just saw on screen.
      const payload = replyPreview
        ? { amount: Number(replyAmountOverride || replyPreview.amount),
            reference: (replyReferenceOverride || replyPreview.reference).trim() }
        : { reply_body: replyText.trim() };
      const { data } = await api.post(`/owners/${drawerOwner.user_id}/statement-reply`, payload);
      const summary = data.partial > 0
        ? `${data.marked} paid + ${data.partial} partial · ₹${Number(data.amount).toLocaleString("en-IN")}`
        : `${data.marked} invoice(s) marked paid · ₹${Number(data.amount).toLocaleString("en-IN")}`;
      toast.success(summary);
      setReplyOpen(false); setReplyText(""); setReplyPreview(null);
      setReplyAmountOverride(""); setReplyReferenceOverride("");
      const [inv, hist] = await Promise.all([
        api.get(`/maintenance-invoices?user_id=${drawerOwner.user_id}`),
        api.get(`/owners/${drawerOwner.user_id}/statement-history`).catch(() => ({ data: [] })),
      ]);
      setDrawerInvoices(inv.data);
      setDrawerHistory(hist.data || []);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setDrawerBusy(false); }
  };

  const submitStatementEmail = async () => {
    if (!drawerOwner) return;
    setDrawerBusy(true);
    try {
      const { data } = await api.post(`/owners/${drawerOwner.user_id}/overdue-statement/send`,
        stmtNote.trim() ? { note: stmtNote.trim() } : {});
      toast.success(`Statement sent — ${data.invoices} invoice(s) totalling ₹${Number(data.total).toLocaleString("en-IN")}`);
      setSendStmtOpen(false); setStmtNote("");
      const [inv, hist] = await Promise.all([
        api.get(`/maintenance-invoices?user_id=${drawerOwner.user_id}`),
        api.get(`/owners/${drawerOwner.user_id}/statement-history`).catch(() => ({ data: [] })),
      ]);
      setDrawerInvoices(inv.data);
      setDrawerHistory(hist.data || []);
      load();
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setDrawerBusy(false); }
  };

  const emailReportNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/common-report/send-now?year=${reportForm.year}&month=${reportForm.month}`);
      toast.success(`Digest emailed to ${data.sent} admin(s)`);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const userLabel = (id) => {
    const u = users.find((x) => x.id === id);
    return u ? `${u.name} · ${u.email}` : id?.slice(0, 8);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="heading text-3xl md:text-4xl">Payments</h1>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={sendBulkReminders} disabled={busy} data-testid="bulk-reminders-btn">
            <Send size={14} className="mr-2" /> {busy ? "Sending…" : "Email all with dues"}
          </Button>
          <Button variant="outline" onClick={runEscalations} disabled={busy} data-testid="run-escalations-btn">
            <Flame size={14} className="mr-2" /> Escalate 60+ days
          </Button>
          <Button variant="outline" onClick={() => { setReport(null); setReportOpen(true); }} data-testid="common-report-btn">
            <BarChart3 size={14} className="mr-2" /> Common maintenance report
          </Button>
          <Button variant="outline" onClick={() => setGenOpen(true)} data-testid="generate-invoices-btn">
            <Wrench size={14} className="mr-2" /> Generate maintenance invoices
          </Button>
          <Button onClick={() => setOpen(true)} data-testid="new-payment-btn">
            <Plus size={14} className="mr-2" /> Capture payment
          </Button>
        </div>
      </div>

      {overdue.length > 0 && (
        <>
          <AgingBar overdueSummary={overdueSummary} bucketFilter={bucketFilter} setBucketFilter={setBucketFilter} />
          <OverdueOwners overdue={overdue} filteredOverdue={filteredOverdue} onOpenTimeline={openTimeline} />
        </>
      )}

      <div className="border border-border rounded-lg bg-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="payments-table">
          <thead className="text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-3">Paid on</th><th className="p-3">User</th><th className="p-3">For</th>
              <th className="p-3 text-right">Amount</th><th className="p-3">Method</th><th className="p-3">Ref</th><th className="p-3">Receipt</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {payments.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-muted-foreground text-xs">No payments recorded yet.</td></tr>}
            {payments.map((p) => (
              <tr key={p.id} data-testid={`row-payment-${p.id}`}>
                <td className="p-3">{p.paid_at}</td>
                <td className="p-3 text-muted-foreground">{userLabel(p.user_id)}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest text-primary font-semibold">{kindLabel(p.kind)}</span></td>
                <td className="p-3 text-right font-medium">{fmt(p.amount)}</td>
                <td className="p-3 text-muted-foreground">{p.method?.replace("_", " ")}</td>
                <td className="p-3 text-muted-foreground text-xs">{p.reference || "—"}</td>
                <td className="p-3">
                  <a href={`${backend}/api/payments/${p.id}/receipt`} target="_blank" rel="noreferrer"
                     className="text-primary text-xs inline-flex items-center gap-1"
                     data-testid={`receipt-${p.id}`}>
                    <Download size={12} /> PDF
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="capture-payment-modal">
          <DialogHeader><DialogTitle>Capture payment</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label>User</Label>
              <Select value={form.user_id} onValueChange={(v) => setForm({ ...form, user_id: v })}>
                <SelectTrigger data-testid="pay-user-select"><SelectValue placeholder="Select owner" /></SelectTrigger>
                <SelectContent>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} · {u.email}</SelectItem>))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Kind</Label>
                <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                  <SelectTrigger data-testid="pay-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plot_purchase">Plot purchase</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Amount (₹)</Label>
                <Input type="number" step="0.01" min="0.01" required value={form.amount}
                       onChange={(e) => setForm({ ...form, amount: e.target.value })}
                       data-testid="pay-amount-input" />
              </div>
            </div>
            <div>
              <Label>Plot (optional)</Label>
              <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                <SelectTrigger data-testid="pay-plot-select"><SelectValue placeholder="No specific plot" /></SelectTrigger>
                <SelectContent>{plots.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.plot_number} · {projects.find((x) => x.id === p.project_id)?.name || ""}</SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Method</Label>
                <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                  <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => (<SelectItem key={m} value={m}>{m.replace("_", " ")}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Reference</Label>
                <Input placeholder="UTR / cheque no." value={form.reference}
                       onChange={(e) => setForm({ ...form, reference: e.target.value })}
                       data-testid="pay-ref-input" />
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea rows={2} value={form.note}
                        onChange={(e) => setForm({ ...form, note: e.target.value })}
                        data-testid="pay-note-input" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy || !form.user_id || !form.amount} data-testid="pay-submit-btn">
                {busy ? "Capturing…" : "Capture & email receipt"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={genOpen} onOpenChange={(o) => { setGenOpen(o); if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl" data-testid="generate-modal">
          <DialogHeader>
            <DialogTitle>{preview ? "Bulk invoice preview" : "Generate maintenance invoices"}</DialogTitle>
          </DialogHeader>
          {!preview && (
            <form onSubmit={submitPreview} className="space-y-3">
              <p className="text-xs text-muted-foreground">Preview shows exactly who would be invoiced (owned plots + common-maintenance owners) with amount and any missing-data warnings. Nothing is written until you confirm.</p>
              <div>
                <Label>Project</Label>
                <Select value={gen.project_id} onValueChange={(v) => setGen({ ...gen, project_id: v })}>
                  <SelectTrigger data-testid="gen-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => (
                    <SelectItem key={p.id} value={p.id}>{p.name}{p.maintenance_rate_per_sqft ? ` · ₹${p.maintenance_rate_per_sqft}/sq ft` : " · no rate set"}</SelectItem>
                  ))}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Period start</Label><Input type="date" required value={gen.period_start} onChange={(e) => setGen({ ...gen, period_start: e.target.value })} data-testid="gen-start-input" /></div>
                <div><Label>Period end</Label><Input type="date" required value={gen.period_end} onChange={(e) => setGen({ ...gen, period_end: e.target.value })} data-testid="gen-end-input" /></div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={busy || !gen.project_id || !gen.period_start} data-testid="gen-preview-btn">
                  <Eye size={14} className="mr-2" />{busy ? "Loading…" : "Preview"}
                </Button>
              </DialogFooter>
            </form>
          )}
          {preview && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{preview.project}</span> · rate {preview.rate ? `₹${preview.rate}/sq ft` : "not set"} · {preview.period_start} → {preview.period_end}
              </div>
              {(() => {
                const rows = preview.rows || [];
                const billable = rows.filter((r) => r.user_id && r.amount > 0 && !r.issue && !r.already_invoiced);
                const total = billable.reduce((s, r) => s + Number(r.amount || 0), 0);
                const skipped = rows.length - billable.length;
                return (
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="rounded px-2 py-1 bg-primary/10 text-primary font-medium" data-testid="preview-billable-count">{billable.length} billable · ₹{total.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
                    <span className="rounded px-2 py-1 bg-secondary text-secondary-foreground">{skipped} skipped</span>
                  </div>
                );
              })()}
              <div className="border border-border rounded-lg overflow-x-auto max-h-[45vh] overflow-y-auto">
                <table className="w-full text-xs" data-testid="preview-table">
                  <thead className="text-left uppercase tracking-widest text-[10px] text-muted-foreground bg-muted/40 sticky top-0">
                    <tr>
                      <th className="p-2">Owner</th>
                      <th className="p-2">Plot / Kind</th>
                      <th className="p-2 text-right">Sq ft</th>
                      <th className="p-2 text-right">Amount</th>
                      <th className="p-2">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(preview.rows || []).map((r, i) => {
                      const blocked = !!r.issue || !r.user_id || r.amount <= 0;
                      const skip = r.already_invoiced;
                      return (
                        <tr key={r.user_id ? `${r.user_id}-${r.kind}` : `preview-${i}`} className={blocked ? "opacity-60" : ""} data-testid={`preview-row-${i}`}>
                          <td className="p-2">{r.user_name || "—"}</td>
                          <td className="p-2">
                            {r.plot_number ? <span>{r.plot_number}</span> : <span className="italic text-muted-foreground">common maintenance</span>}
                            <span className="ml-1 text-[10px] uppercase tracking-widest text-muted-foreground">· {r.kind === "with_plot" ? "plot" : "common"}</span>
                          </td>
                          <td className="p-2 text-right">{Number(r.size_sqft || 0).toLocaleString("en-IN")}</td>
                          <td className="p-2 text-right font-medium">{fmt(r.amount)}</td>
                          <td className="p-2">
                            {blocked && <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle size={12} /> {r.issue || "not billable"}</span>}
                            {!blocked && skip && <span className="inline-flex items-center gap-1 text-muted-foreground">already invoiced</span>}
                            {!blocked && !skip && <span className="inline-flex items-center gap-1 text-primary"><CheckCircle2 size={12} /> will bill</span>}
                          </td>
                        </tr>
                      );
                    })}
                    {(preview.rows || []).length === 0 && (
                      <tr><td colSpan="5" className="p-4 text-center text-muted-foreground">No rows — set owners &amp; common-maintenance flags first.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPreview(null)} data-testid="preview-back-btn">Back</Button>
                <Button type="button" variant="outline" onClick={downloadPreviewCSV} data-testid="preview-csv-btn">
                  <FileText size={14} className="mr-2" /> Download CSV
                </Button>
                <Button type="button" onClick={confirmGenerate} disabled={busy} data-testid="gen-submit-btn">
                  <IndianRupee size={14} className="mr-2" />{busy ? "Generating…" : "Confirm & generate"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Common maintenance report */}
      <Dialog open={reportOpen} onOpenChange={(o) => { setReportOpen(o); if (!o) setReport(null); }}>
        <DialogContent className="max-w-3xl" data-testid="report-modal">
          <DialogHeader><DialogTitle>Common maintenance report</DialogTitle></DialogHeader>
          <form onSubmit={runReport} className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Year</Label>
              <Input type="number" min="2024" max="2100" value={reportForm.year}
                     onChange={(e) => setReportForm({ ...reportForm, year: Number(e.target.value) })}
                     className="w-28" data-testid="report-year-input" />
            </div>
            <div>
              <Label>Month</Label>
              <Select value={String(reportForm.month)} onValueChange={(v) => setReportForm({ ...reportForm, month: Number(v) })}>
                <SelectTrigger className="w-36" data-testid="report-month-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m, i) => (
                    <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy} data-testid="report-run-btn">
              <BarChart3 size={14} className="mr-2" />{busy ? "Loading…" : "Run report"}
            </Button>
          </form>
          {report && (
            <div className="space-y-4 mt-2">
              <div className="flex items-center justify-between flex-wrap gap-2 text-xs">
                <div className="text-muted-foreground">
                  Period <span className="text-foreground font-medium">{report.period_start} → {report.period_end}</span>
                  {" · "}<span className="text-primary font-medium" data-testid="report-total">{report.grand_count} owner(s) · {fmt(report.grand_total)}</span>
                </div>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={downloadReportCSV}
                          disabled={!(report.projects || []).length} data-testid="report-csv-btn">
                    <FileText size={12} className="mr-1" /> Download CSV
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={emailReportNow}
                          disabled={busy} data-testid="report-email-btn">
                    <Mail size={12} className="mr-1" /> Email digest now
                  </Button>
                </div>
              </div>
              {(report.projects || []).length === 0 && (
                <div className="text-sm text-muted-foreground p-6 border border-dashed border-border rounded-lg text-center">No common-maintenance invoices in this month.</div>
              )}
              {(report.projects || []).map((proj) => (
                <div key={proj.project_id} className="border border-border rounded-lg overflow-hidden" data-testid={`report-project-${proj.project_id}`}>
                  <div className="px-3 py-2 bg-muted/40 flex items-center justify-between text-xs">
                    <span className="font-medium text-sm">{proj.project_name}</span>
                    <span className="text-muted-foreground">{proj.count} owner(s) · <span className="text-primary font-semibold">{fmt(proj.total)}</span> · due {fmt(proj.due)} · paid {fmt(proj.paid)}</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead className="text-left uppercase tracking-widest text-[10px] text-muted-foreground">
                      <tr><th className="p-2">Owner</th><th className="p-2">Email</th><th className="p-2 text-right">Amount</th><th className="p-2">Status</th></tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {proj.rows.map((r, i) => (
                        <tr key={r.user_id || `report-${proj.project_id}-${i}`} data-testid={`report-row-${proj.project_id}-${i}`}>
                          <td className="p-2">{r.user_name}</td>
                          <td className="p-2 text-muted-foreground">{r.user_email || "—"}</td>
                          <td className="p-2 text-right font-medium">{fmt(r.amount)}</td>
                          <td className="p-2"><span className={`text-[10px] uppercase tracking-widest ${r.status === "paid" ? "text-primary" : "text-amber-700"}`}>{r.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <TimelineDrawer
        drawerOwner={drawerOwner}
        drawerInvoices={drawerInvoices}
        drawerHistory={drawerHistory}
        drawerBusy={drawerBusy}
        eventFilter={eventFilter}
        setEventFilter={setEventFilter}
        onClose={() => { setDrawerOwner(null); setDrawerInvoices([]); }}
        onOpenSendStmt={openSendStmt}
        onOpenReply={() => { setReplyText(""); setReplyOpen(true); }}
      />

      <SendStatementModal
        open={sendStmtOpen}
        onOpenChange={setSendStmtOpen}
        ownerEmail={drawerOwner?.user_email}
        stmtNote={stmtNote}
        setStmtNote={setStmtNote}
        customTemplates={customTemplates}
        onSaveTemplate={saveCustomTemplate}
        onSubmit={submitStatementEmail}
        busy={drawerBusy}
      />

      <ReplyModal
        open={replyOpen}
        onOpenChange={setReplyOpen}
        replyText={replyText}
        setReplyText={setReplyText}
        replyPreview={replyPreview}
        setReplyPreview={setReplyPreview}
        replyAmountOverride={replyAmountOverride}
        setReplyAmountOverride={setReplyAmountOverride}
        replyReferenceOverride={replyReferenceOverride}
        setReplyReferenceOverride={setReplyReferenceOverride}
        busy={drawerBusy}
        onPreview={previewReply}
        onRePreview={rePreviewWithOverride}
        onSubmit={submitReply}
      />

    </div>
  );
}
